import * as R from 'ramda';
import { Assessment } from 'types/api/envScales/Assessment';
import {
  ObservationValidations,
  SectionValidation,
  SubsectionValidation,
  ValidationError
} from 'types/api/envScales/ObservationValidations';

type PAGE_STATUS = 'pending' | 'completed' | 'error' | 'disabled';

type StatusByPage = {
  details: PAGE_STATUS;
  environments: PAGE_STATUS;
  evidence: PAGE_STATUS;
  summary: PAGE_STATUS;
  [key: number]: PAGE_STATUS;
};

type ValidationsContextValue = {
  status: StatusByPage;
  validate: () => Promise<boolean>;
  errors: Record<string, ValidationError>;
  sections: ObservationValidations['sections'] | undefined;
  showErrorStatus: boolean;
  resolvedPaths: Record<string, boolean>;
  reportFieldResolution: (path: string, isResolved: boolean) => void;
  pendingPathname: string | null;
};

export const initialValue: ValidationsContextValue = {
  status: {
    details: 'pending',
    environments: 'pending',
    1: 'pending',
    2: 'pending',
    3: 'pending',
    4: 'pending',
    5: 'pending',
    6: 'pending',
    evidence: 'pending',
    summary: 'disabled'
  },
  validate: () => Promise.resolve(false),
  errors: {},
  sections: undefined,
  showErrorStatus: false,
  resolvedPaths: {},
  reportFieldResolution: () => {},
  pendingPathname: null
};

function pathMatchesPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}.`);
}

// Zero matching paths deliberately returns false, not vacuously true.
function isPrefixResolved(
  errors: Record<string, ValidationError>,
  resolvedPaths: Record<string, boolean>,
  prefix: string
): boolean {
  const matchingPaths = Object.keys(errors).filter(path =>
    pathMatchesPrefix(path, prefix)
  );

  if (matchingPaths.length === 0) {
    return false;
  }

  return matchingPaths.every(path => resolvedPaths[path]);
}

function hasLiveInvalidField(
  resolvedPaths: Record<string, boolean>,
  prefix: string
): boolean {
  return Object.keys(resolvedPaths).some(
    path => pathMatchesPrefix(path, prefix) && resolvedPaths[path] === false
  );
}

export type ResolveContext = {
  errors: Record<string, ValidationError>;
  resolvedPaths: Record<string, boolean>;
  prefix: string;
};

export function getSectionStatus(
  section: SectionValidation | SubsectionValidation | undefined,
  showErrorStatus: boolean,
  resolve?: ResolveContext
): PAGE_STATUS {
  if (!section) {
    return 'completed';
  }

  if (!showErrorStatus) {
    return section.valid ? 'completed' : 'pending';
  }

  if (resolve && hasLiveInvalidField(resolve.resolvedPaths, resolve.prefix)) {
    return 'error';
  }

  if (section.valid) {
    return 'completed';
  }

  if (
    resolve &&
    isPrefixResolved(resolve.errors, resolve.resolvedPaths, resolve.prefix)
  ) {
    return 'completed';
  }

  return 'error';
}

export function getSubsectionStatus(
  section: SectionValidation,
  subsectionKey: string,
  showErrorStatus: boolean,
  resolve?: ResolveContext
): PAGE_STATUS {
  const subsection = section?.subsections?.[subsectionKey];
  return getSectionStatus(subsection, showErrorStatus, resolve);
}

function getSummaryStatus(statusByPage: StatusByPage, assessment: Assessment) {
  const nonSummaryPages = R.omit(['summary'], statusByPage);
  const pages = R.keys(nonSummaryPages);
  const returnStatus =
    assessment.status === 'completed' ? 'completed' : 'pending';

  return pages.every(page => statusByPage[page] === 'completed')
    ? returnStatus
    : 'disabled';
}

export function getStatusByPage(
  validations: ObservationValidations,
  assessment: Assessment,
  showErrorStatus = true,
  resolvedPaths: Record<string, boolean> = {}
) {
  if (!validations?.sections) {
    return initialValue.status;
  }

  const statusByPage = R.clone(initialValue.status);
  const { sections, errors } = validations;
  const resolveFor = (prefix: string): ResolveContext => ({
    errors,
    resolvedPaths,
    prefix
  });

  const cycles = [1, 2, 3, 4, 5, 6];
  cycles.forEach(cycle => {
    statusByPage[cycle] = getSectionStatus(
      sections.cycles?.[cycle],
      showErrorStatus,
      resolveFor(`cycles.${cycle}`)
    );
  });

  statusByPage.environments = getSectionStatus(
    sections.environment,
    showErrorStatus,
    resolveFor('environment')
  );
  statusByPage.evidence = getSectionStatus(
    sections.evidence,
    showErrorStatus,
    resolveFor('evidence')
  );
  statusByPage.summary = getSummaryStatus(statusByPage, assessment);
  statusByPage.details = getSectionStatus(
    sections.assessment,
    showErrorStatus,
    resolveFor('assessment')
  );

  return statusByPage;
}

export function isSummaryEnabled(
  validations: ObservationValidations,
  assessment: Assessment
) {
  const statusByPage = getStatusByPage(validations, assessment);
  const summaryStatus = getSummaryStatus(statusByPage, assessment);
  return summaryStatus === 'completed' || summaryStatus === 'pending';
}
